// Push Notification Service for Retirement Calculator
// Handles web push notifications for important updates and reminders
// Last updated: January 2025

export const PushNotificationService = {
    // Initialize push notifications
    async initialize() {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.log('Push messaging not supported');
            return false;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            const permission = await this.requestPermission();
            
            if (permission === 'granted') {
                const subscription = await this.getSubscription();
                if (!subscription) {
                    await this.subscribe();
                }
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Push notification initialization failed:', error);
            return false;
        }
    },

    // Request notification permission
    async requestPermission() {
        const permission = await Notification.requestPermission();
        return permission;
    },

    // Subscribe to push notifications
    async subscribe() {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(this.getVapidPublicKey())
            });
            
            await this.saveSubscription(subscription);
            return subscription;
        } catch (error) {
            console.error('Push subscription failed:', error);
            return null;
        }
    },

    // Get current subscription
    async getSubscription() {
        const registration = await navigator.serviceWorker.ready;
        return await registration.pushManager.getSubscription();
    },

    // Unsubscribe from notifications
    async unsubscribe() {
        const subscription = await this.getSubscription();
        if (subscription) {
            await subscription.unsubscribe();
            await this.removeSubscription();
            return true;
        }
        return false;
    },

    // Send notification
    async sendNotification(title, options = {}) {
        if (Notification.permission === 'granted') {
            const notification = new Notification(title, {
                icon: '/icons/icon-192x192.png',
                badge: '/icons/icon-72x72.png',
                ...options
            });
            
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
            
            return notification;
        }
    },

    // Schedule reminder notifications
    scheduleReminders() {
        // Check for rate updates
        this.scheduleRateUpdateCheck();
        
        // Check for contribution deadlines
        this.scheduleContributionReminders();
        
        // Check for compliance deadlines
        this.scheduleComplianceReminders();
    },

    // Get VAPID public key (would be from environment in production)
    getVapidPublicKey() {
        return 'BEl62iUYgUivxIkv69yViEuiBIa40HI0lFc8OeLgV9m8RwQHhVz8HnQJZvKqLmNpQrStUvWxYz'; // Placeholder
    },

    // Convert VAPID key
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    },

    // Save subscription to localStorage (would be to server in production)
    async saveSubscription(subscription) {
        localStorage.setItem('pushSubscription', JSON.stringify(subscription));
    },

    // Remove subscription
    async removeSubscription() {
        localStorage.removeItem('pushSubscription');
    },

    // Schedule rate update checks
    scheduleRateUpdateCheck() {
        // Check weekly for rate updates
        setInterval(async () => {
            const lastCheck = localStorage.getItem('lastRateCheck');
            const now = new Date();
            
            if (!lastCheck || (now - new Date(lastCheck)) > 7 * 24 * 60 * 60 * 1000) {
                await this.checkForRateUpdates();
                localStorage.setItem('lastRateCheck', now.toISOString());
            }
        }, 24 * 60 * 60 * 1000); // Daily check
    },

    // Check for rate updates
    async checkForRateUpdates() {
        try {
            // This would check an API for rate updates
            const hasUpdates = await this.fetchRateUpdates();
            
            if (hasUpdates) {
                await this.sendNotification('Rate Updates Available', {
                    body: 'New superannuation rates and thresholds are available',
                    tag: 'rate-update',
                    requireInteraction: false
                });
            }
        } catch (error) {
            console.error('Rate update check failed:', error);
        }
    },

    // Fetch rate updates (placeholder)
    async fetchRateUpdates() {
        // This would make an API call to check for updates
        return false;
    },

    // Schedule contribution reminders
    scheduleContributionReminders() {
        // Check monthly for contribution opportunities
        setInterval(async () => {
            const lastCheck = localStorage.getItem('lastContributionCheck');
            const now = new Date();
            
            if (!lastCheck || (now - new Date(lastCheck)) > 30 * 24 * 60 * 60 * 1000) {
                await this.checkContributionOpportunities();
                localStorage.setItem('lastContributionCheck', now.toISOString());
            }
        }, 24 * 60 * 60 * 1000); // Daily check
    },

    // Check contribution opportunities
    async checkContributionOpportunities() {
        try {
            const opportunities = await this.analyzeContributionOpportunities();
            
            if (opportunities.length > 0) {
                await this.sendNotification('Contribution Opportunities', {
                    body: `You have ${opportunities.length} contribution opportunities available`,
                    tag: 'contribution-reminder',
                    requireInteraction: false
                });
            }
        } catch (error) {
            console.error('Contribution check failed:', error);
        }
    },

    // Analyze contribution opportunities (placeholder)
    async analyzeContributionOpportunities() {
        // This would analyze user's contribution caps and opportunities
        return [];
    },

    // Schedule compliance reminders
    scheduleComplianceReminders() {
        // Check for SMSF compliance deadlines
        setInterval(async () => {
            const lastCheck = localStorage.getItem('lastComplianceCheck');
            const now = new Date();
            
            if (!lastCheck || (now - new Date(lastCheck)) > 7 * 24 * 60 * 60 * 1000) {
                await this.checkComplianceDeadlines();
                localStorage.setItem('lastComplianceCheck', now.toISOString());
            }
        }, 24 * 60 * 60 * 1000); // Daily check
    },

    // Check compliance deadlines
    async checkComplianceDeadlines() {
        try {
            const deadlines = await this.getComplianceDeadlines();
            
            if (deadlines.length > 0) {
                await this.sendNotification('Compliance Deadlines', {
                    body: `You have ${deadlines.length} compliance deadlines approaching`,
                    tag: 'compliance-reminder',
                    requireInteraction: true
                });
            }
        } catch (error) {
            console.error('Compliance check failed:', error);
        }
    },

    // Get compliance deadlines (placeholder)
    async getComplianceDeadlines() {
        // This would check SMSF compliance deadlines
        return [];
    }
};

export default PushNotificationService;
